/**
 * Tagging helpers.
 *
 * A site defines its tag groups in `artistConfig.artist.tagGroups`. Groups and
 * tags carry a stable opaque `id` plus a presentational `label`:
 *
 *   [ { id: 'g_a1b2', label: 'Subjects', tags: [
 *         { id: 't_x1y2', label: 'portrait' },
 *         { id: 't_x3y4', label: 'still life' } ] } ]
 *
 * Each work stores tag *ids*, keyed by group id:
 *
 *   work.tags = { g_a1b2: ['t_x1y2', 't_x3y4'] }
 *
 * Because works reference ids, renaming a group/tag is a single edit to its
 * `label` in config - no work documents change, and nothing is orphaned.
 * The `label` is resolved for display only (see resolveWorkTags / tagLabel).
 */

function randomId(prefix) {
  return `${prefix}_${Math.random().toString(36).slice(2, 8)}${Date.now().toString(36).slice(-3)}`
}

export function makeGroupId() {
  return randomId('g')
}

export function makeTagId() {
  return randomId('t')
}

/**
 * Coerce raw config into a clean array of { id, label, tags: [{id,label}] }.
 * Tolerates undefined/malformed entries and stamps any missing ids (defensive -
 * normal data always has them).
 */
export function normalizeTagGroups(raw) {
  if (!Array.isArray(raw)) return []
  const groups = []
  for (const g of raw) {
    if (!g || typeof g.label !== 'string') continue
    const label = g.label.trim()
    if (!label) continue
    const tags = Array.isArray(g.tags)
      ? g.tags
          .filter(t => t && typeof t.label === 'string' && t.label.trim())
          .map(t => ({ id: t.id || makeTagId(), label: t.label.trim() }))
      : []
    groups.push({ id: g.id || makeGroupId(), label, tags })
  }
  return groups
}

/**
 * Find a group object by its id.
 */
export function groupById(tagGroups, groupId) {
  return (tagGroups || []).find(g => g.id === groupId) || null
}

/**
 * Resolve a tag id to its current label, or '' if the tag no longer exists.
 */
export function tagLabel(tagGroups, groupId, tagId) {
  const group = groupById(tagGroups, groupId)
  return group?.tags.find(t => t.id === tagId)?.label || ''
}

/**
 * A work's tag ids for a given group id, always as an array.
 */
export function workTagsForGroup(work, groupId) {
  const tags = work?.tags?.[groupId]
  return Array.isArray(tags) ? tags : []
}

/**
 * Resolve a work's stored tag ids into display rows, ordered by config:
 *   [ { groupId, groupLabel, tags: [{ id, label }] } ]
 * Only groups/tags that still exist in config and are present on the work appear.
 */
export function resolveWorkTags(work, tagGroups) {
  const groups = tagGroups || []
  const result = []
  for (const g of groups) {
    const ids = workTagsForGroup(work, g.id)
    if (!ids.length) continue
    const tags = g.tags.filter(t => ids.includes(t.id))
    if (tags.length) result.push({ groupId: g.id, groupLabel: g.label, tags })
  }
  return result
}

/**
 * Faceted-search predicate: OR within a group, AND across groups.
 * `selected` is a map of { [groupId]: tagId[] }. Groups with an empty
 * selection are ignored. Works on opaque ids - labels are irrelevant here.
 */
export function workMatchesTags(work, selected) {
  if (!selected) return true
  for (const groupId of Object.keys(selected)) {
    const chosen = selected[groupId]
    if (!Array.isArray(chosen) || chosen.length === 0) continue
    const workTags = workTagsForGroup(work, groupId)
    if (!chosen.some(id => workTags.includes(id))) return false
  }
  return true
}

/**
 * True if any group in the selection map has a non-empty array.
 */
export function hasActiveTagFilters(selected) {
  if (!selected) return false
  return Object.values(selected).some(arr => Array.isArray(arr) && arr.length > 0)
}

// Alt text from what the work already has: "Starry Night, 1889. Oil on canvas, landscape"
export function artworkAlt(work, tagGroups) {
  const title = [work?.name, work?.year].filter(Boolean).join(', ')
  const tags = resolveWorkTags(work, tagGroups).flatMap(g => g.tags.map(t => t.label))
  if (!tags.length) return title
  const tagText = tags.join(', ')
  const described = tagText.charAt(0).toUpperCase() + tagText.slice(1)
  return title ? `${title}. ${described}` : described
}
